import React, { useEffect, useCallback, useState } from "react";
import {
  GoogleMap,
  Marker,
  Polyline,
  useGoogleMap,
} from "@react-google-maps/api";
import NearbyTreasuresLayer from "./NearbyTreasuresLayer"; // Import the component type

const Markers = ({ places, setSelectedPlace }) => {
  const map = useGoogleMap();

  const handleMarkerClick = (place, event) => {
    if (!map || !event.domEvent) return;

    // Get the map container DOM node
    const mapDiv = map.getDiv();
    const rect = mapDiv.getBoundingClientRect();
    const { clientX, clientY } = event.domEvent;
    // Calculate position relative to the map container
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    setSelectedPlace(place, { x, y: y - 40 }); // 40px offset above marker
  };

  return (
    <>
      {places.map((place) => {
        let lat = null,
          lng = null;
        if (place.geometry && place.geometry.location) {
          if (typeof place.geometry.location.lat === "function") {
            lat = place.geometry.location.lat();
            lng = place.geometry.location.lng();
          } else {
            lat = place.geometry.location.lat;
            lng = place.geometry.location.lng;
          }
        }
        if (lat && lng) {
          return (
            <Marker
              key={place.place_id}
              position={{ lat, lng }}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
              }}
              title={place.name}
              onClick={(event) => handleMarkerClick(place, event)}
            />
          );
        }
        return null;
      })}
    </>
  );
};

const MapView = ({
  mapKey,
  containerStyle,
  mapCenter,
  onLoad,
  onUnmount,
  mapStyles,
  places,
  routes,
  directions, // <--- 接收 directions prop
  selectedPlace,
  setSelectedPlace,
  userLat,
  userLng,
  heading, // <--- 新增 heading prop
  handleMapClick,
  onMapInteraction, // 新增
  onEndMarkerClick, // 新增 prop
  children, // 新增：接收子元件
}) => {
  const [showFoundSuccess, setShowFoundSuccess] = useState(false);

  // 移除內部 state，直接依賴 props 計算路徑

  // 從路線數據中提取路徑點的函數
  const extractPath = useCallback(() => {
    //console.log("Extracting path from routes/directions...");

    // 嘗試從 routes 中提取路徑
    if (
      routes &&
      routes.length > 0 &&
      routes[0].path &&
      routes[0].path.length > 0
    ) {
      //console.log("Found path in routes with", routes[0].path.length, "points");
      return routes[0].path;
    }

    // 嘗試從 directions 中提取路徑
    if (directions && directions.routes && directions.routes.length > 0) {
      const route = directions.routes[0];

      try {
        // 如果有 overview_polyline 並且是字符串格式
        if (route.overview_polyline) {
          if (typeof route.overview_polyline === "string") {
            //console.log("Decoding string polyline");
            return window.google.maps.geometry.encoding.decodePath(
              route.overview_polyline
            );
          }
          // 如果有 overview_polyline.points
          else if (route.overview_polyline.points) {
            //console.log("Decoding polyline.points");
            return window.google.maps.geometry.encoding.decodePath(
              route.overview_polyline.points
            );
          }
        }

        // 如果上述方法都失敗，嘗試從 legs 構建路徑
        if (route.legs && route.legs.length > 0) {
          let legPath = [];
          route.legs.forEach((leg) => {
            if (leg.steps && leg.steps.length > 0) {
              // 添加每個 step 的起點
              leg.steps.forEach((step) => {
                if (step.start_location) legPath.push(step.start_location);
                // 如果 step 有詳細的路徑點，也加入
                if (step.path && Array.isArray(step.path)) {
                  legPath = [...legPath, ...step.path];
                }
              });

              // 添加最後一個 step 的終點
              const lastStep = leg.steps[leg.steps.length - 1];
              if (lastStep && lastStep.end_location) {
                legPath.push(lastStep.end_location);
              }
            }
          });

          if (legPath.length > 0) {
            //console.log("Built path from legs with", legPath.length, "points");
            return legPath;
          }
        }
      } catch (error) {
        console.error("Error extracting path:", error);
      }
    }

    // 如果都失敗了，返回空數組
    // console.warn("Failed to extract any path");
    return [];
  }, [routes, directions]);

  // 直接在每次 render 時計算路徑，確保與 props 同步
  const path = extractPath();

  // Google Maps 控制按鈕強制定位
  useEffect(() => {
    // 直接隱藏地圖攝影機控制項
    const hideGmControl = () => {
      const controls = document.querySelectorAll(
        '.gm-control-active[title*="控制項"], .gm-control-active[title*="Control"]'
      );
      controls.forEach((el) => {
        el.style.display = "none";
      });
    };
    hideGmControl();
    const observer = new MutationObserver(hideGmControl);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // 在 children 傳入 onReportSuccess callback
  const childrenWithProps = React.Children.map(children, (child) => {
    if (!child) return child;
    // 修正：直接比較 component type，避免 production build minification 問題
    if (child.type === NearbyTreasuresLayer) {
      return React.cloneElement(child, {
        onReportSuccess: (result) => {
          console.log("[DEBUG] onReportSuccess triggered with result:", result);
          // Use functional update to avoid stale state issues from closures.
          setShowFoundSuccess((currentValue) => {
            console.log(
              `[DEBUG] Updating showFoundSuccess from ${currentValue} to true`
            );
            return true;
          });
          setTimeout(() => {
            setShowFoundSuccess((currentValue) => {
              console.log(
                `[DEBUG] Hiding success message. Current value: ${currentValue}`
              );
              return false;
            });
          }, 1800);
        },
      });
    }
    return child;
  });

  useEffect(() => {
    if (typeof window !== "undefined") window.__mapViewMounted = true;
    return () => {
      if (typeof window !== "undefined") window.__mapViewMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") window.__showFoundSuccessValue = showFoundSuccess;
    console.log("[DEBUG] showFoundSuccess value:", showFoundSuccess);
  }, [showFoundSuccess]);

  return (
    <div className="relative w-full h-full">
      {showFoundSuccess && (
        (() => {
          console.log("[DEBUG] Rendering success toast. showFoundSuccess:", showFoundSuccess);
          return (
            <div
              id="treasure-success-toast"
              style={{
                position: "fixed",
                top: "20px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2147483647,
                background: "#2b8c47",
                color: "#fff",
                fontWeight: "bold",
                fontSize: 20,
                borderRadius: 16,
                boxShadow: "0 4px 16px #2b8c4722",
                padding: "16px 32px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                animation: "fadeInCard 0.18s cubic-bezier(.4,0,.2,1)",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="16" fill="#fffbe6" />
                <path
                  d="M10 17l4 4 8-8"
                  stroke="#2b8c47"
                  strokeWidth="3"
                  fill="none"
                />
              </svg>
              <span>恭喜！你已成功尋獲寶藏！</span>
            </div>
          );
        })()
      )}
      <GoogleMap
        key={mapKey}
        mapContainerStyle={{ ...containerStyle, marginBottom: 120 }}
        center={mapCenter}
        zoom={15}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          disableDefaultUI: false, // 顯示預設控制元件
          zoomControl: true, // 仍需開啟，否則攝影機控制項不會出現
          styles: mapStyles,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          scaleControl: false,
          gestureHandling: "greedy", // 允許單指拖曳地圖
        }}
        onClick={handleMapClick}
        onDragStart={onMapInteraction}
        onZoomChanged={onMapInteraction}
      >
        {/* 隱藏放大/縮小按鈕及其外層容器，僅保留地圖攝影機控制項 */}
        <style>{`
          button[aria-label="放大"],
          button[title="放大"],
          button[aria-label="縮小"],
          button[title="縮小"],
          /* 隱藏外層控制區塊（寬40px且高度>40px） */
          div[style*="width: 40px;"][style*="height: 81px;"] {
            display: none !important;
          }
        `}</style>
        <style>{`
        button[aria-label="Map camera controls"],
        button[title="Map camera controls"] {
          display: none !important;
        }
      `}</style>

        {/* 用戶當前位置 marker（含方向箭頭） */}
        {userLat && userLng && (
          <Marker
            position={{ lat: userLat, lng: userLng }}
            icon={
              window.google
                ? {
                    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 5,
                    fillColor: "#4285F4",
                    fillOpacity: 1,
                    strokeColor: "#fff",
                    strokeWeight: 2,
                    rotation: (360 - heading) % 360 || 0, // 修正：將 heading 轉換為 Google Maps 標準
                    anchor: new window.google.maps.Point(0, 2),
                  }
                : undefined
            }
            title={`你的位置${heading ? ` (朝向${heading}°)` : ""}`}
          />
        )}

        {/* 景點 marker */}
        <Markers places={places} setSelectedPlace={setSelectedPlace} />

        {/* 自定義點 marker */}
        {selectedPlace &&
          selectedPlace.isCustom &&
          selectedPlace.geometry &&
          selectedPlace.geometry.location && (
            <Marker
              position={{
                lat: selectedPlace.geometry.location.lat,
                lng: selectedPlace.geometry.location.lng,
              }}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              }}
              title="自定義點"
            />
          )}

        {/* --- 統一路線渲染 --- */}
        {/* 僅使用 Polyline 根據 path 狀態來繪製路線 */}
        {path.length > 1 && (
          <Polyline
            key={JSON.stringify(path)} // 使用路徑數據作為 key，強制 React 重繪
            path={path}
            options={{
              strokeColor: "#FF0000",
              strokeOpacity: 0.8,
              strokeWeight: 5,
            }}
          />
        )}

        {/* 保留自訂的終點 marker，但移除起點藍點 */}
        {(() => {
          if (
            routes &&
            routes.length > 0 &&
            routes[0].path &&
            routes[0].path.length > 0
          ) {
            const endLocation = routes[0].path[routes[0].path.length - 1];

            if (!endLocation) return null; // 防呆

            // 兼容 LatLng 物件和普通物件
            const lat =
              typeof endLocation.lat === "function"
                ? endLocation.lat()
                : endLocation.lat;
            const lng =
              typeof endLocation.lng === "function"
                ? endLocation.lng()
                : endLocation.lng;

            return (
              <>
                {/* 終點標記 */}
                <Marker
                  position={{ lat, lng }}
                  icon={{
                    url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                  }}
                  title="終點"
                  onClick={onEndMarkerClick} // 新增：點擊終點 marker 會觸發
                />
              </>
            );
          }
          return null;
        })()}

        {/* 渲染子元件，例如 NearbyTreasuresLayer */}
        {childrenWithProps}
      </GoogleMap>
    </div>
  );
};

export default MapView;
